import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';
import { useLanguage } from '../../i18n/LanguageContext';
const WHATSAPP_URL = 'https://wa.me/77010346443?text=%D1%85%D0%BE%D1%87%D1%83%20%D0%B4%D0%B5%D0%BC%D0%BE%20Meelo';
type Tab = 'web' | 'mobile';
function PillSwitch({
  active,
  onChange
}: {
  active: Tab;
  onChange: (t: Tab) => void;
}) {
  const {
    t
  } = useLanguage();
  return <div className="inline-flex items-center rounded-full bg-white/80 backdrop-blur-md border border-gray-200 p-1 shadow-md" data-id="element-103" data-mp="u7eardi9w789">
      <button onClick={() => onChange('mobile')} className={`relative px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${active === 'mobile' ? 'bg-[#7C4DFF] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-104" data-mp="131klqckqxkrs">
        {t('dashboard.switch_mobile')}
      </button>
      <button onClick={() => onChange('web')} className={`relative px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${active === 'web' ? 'bg-[#7C4DFF] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-105" data-mp="181oufgfexb7g">
        {t('dashboard.switch_web')}
      </button>
    </div>;
}
function CtaPair() {
  const {
    t
  } = useLanguage();
  return <div className="mt-8 flex flex-col sm:flex-row items-start gap-3" data-id="element-106" data-mp="1frr9zgbkbn5r">
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-3 rounded-full bg-[#7C4DFF] text-white font-medium text-base shadow-lg hover:bg-[#651FFF] transition-all" data-id="element-107" data-mp="1fowqps4ac8dx">
        {t('dashboard.cta_more')}
      </a>
      <div className="flex flex-col gap-1" data-id="element-108" data-mp="1zeywy2iru0vg">
        <span className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium text-gray-600 bg-gray-100 border border-gray-200" data-id="element-109" data-mp="1zepa2nt0w9tz">
          {t('dashboard.price')}
        </span>
        <span className="text-xs text-gray-400 px-1" data-id="element-110" data-mp="28gonz78qqxsp">
          {t('dashboard.price_vat')}
        </span>
      </div>
    </div>;
}
export function DashboardSection() {
  const {
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState<Tab>('web');
  const previewRef = useRevealOnScroll<HTMLDivElement>(0, 0.15);
  const benefits = [{
    text: t('dashboard.benefit_1')
  }, {
    text: t('dashboard.benefit_2')
  }, {
    text: t('dashboard.benefit_3')
  }, {
    text: t('dashboard.benefit_4')
  }, {
    text: t('dashboard.benefit_5')
  }];
  return <section className="relative pt-24 pb-6 overflow-hidden" id="solutions" style={{
    background: 'linear-gradient(180deg, #7D4EFF 0%, #6E3EF0 100%)'
  }} data-id="element-111" data-mp="n2a916nadbq0">
      {/* Subtle background blobs for depth */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true" data-id="element-112" data-mp="32tfytae3n5m4">
        <div style={{
        position: 'absolute',
        top: '10%',
        left: '-10%',
        width: 500,
        height: 500,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)',
        filter: 'blur(80px)'
      }} data-id="element-113" data-mp="2xvehov08hgda" />
        <div style={{
        position: 'absolute',
        bottom: '5%',
        right: '-5%',
        width: 400,
        height: 400,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)',
        filter: 'blur(60px)'
      }} data-id="element-114" data-mp="14ifrb6plryk5" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-115" data-mp="ln7ig791d09y">
        {/* Section intro */}
        <div className="text-center mb-16" data-id="element-116" data-mp="pp2lphaj1nqd">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-white/70 mb-3" data-id="element-117" data-mp="2fz5sucj3027f">
            {t('dashboard.label')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white" data-id="element-118" data-mp="2jyocl4z6278o">
            {t('dashboard.title')}
          </h2>
        </div>

        {/* Module card — white */}
        <div className="rounded-3xl border border-gray-100 bg-white shadow-xl overflow-hidden" data-id="element-119" data-mp="1qc1nlha4x4ab">
          <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3" data-id="element-120" data-mp="1lace462k8yoc">
            <span className="text-xs font-bold text-gray-300 tracking-widest font-mono" data-id="element-121" data-mp="1gcklf9u3d8j3">
              01 / 05
            </span>
            <span className="text-xs font-semibold tracking-widest uppercase text-[#7C4DFF]" data-id="element-122" data-mp="1cdbuecqfror8">
              {t('dashboard.card_label')}
            </span>
          </div>
          <div className="p-6 lg:p-10" data-id="element-123" data-mp="12m7cb45grns6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center lg:min-h-[520px]" data-id="element-124" data-mp="xmmhn5xbu1l9">
              {/* Preview — LEFT on desktop */}
              <div className="relative lg:order-1" data-id="element-125" data-mp="sn040vdl6jbx">
                <div ref={previewRef} className="reveal-visual visual-hover-lift relative min-h-[360px] sm:min-h-[420px] flex items-center justify-center" data-id="element-126" data-mp="oleh0h7c8oo0">
                  <div className={`w-full transition-all duration-300 ${activeTab === 'web' ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'}`} data-id="element-127" data-mp="34a3kq1mhn413">
                    <div className="rounded-2xl shadow-2xl border border-gray-200 overflow-hidden bg-white" data-id="element-128" data-mp="3e9ayur11r2p9">
                      <div className="h-8 bg-gray-50 border-b border-gray-100 flex items-center px-4 space-x-2" data-id="element-129" data-mp="2gmbvjxfumwie">
                        <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-130" data-mp="2lkwxc1ar1o8y" />
                        <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-131" data-mp="2qj6qy7gtsgsg" />
                        <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-132" data-mp="2v9x0lwwab5sy" />
                      </div>
                      <img src="https://cdn.magicpatterns.com/uploads/7Tan4tCoZC26N1PHqU6F5U/панель_для_руководителя.png" alt="Дашборд руководителя — веб" className="w-full h-auto" data-id="element-133" data-mp="1xvb03jdxtj3l" />
                    </div>
                  </div>
                  <div className={`w-full flex justify-center transition-all duration-300 ${activeTab === 'mobile' ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'}`} data-id="element-134" data-mp="1qqjop9g43rzh">
                    <img src="https://cdn.magicpatterns.com/uploads/smrKEtBgxHCGSuzY8xQDnA/Mockuuups_Free_iPhone_Hand_Mockup.png" alt="Мобильное приложение Meelo" className="max-h-[420px] w-auto object-contain drop-shadow-2xl" data-id="element-135" data-mp="1zkoia0mmrhip" />
                  </div>
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center z-10" data-id="element-136" data-mp="1gtop0n83rljm">
                    <PillSwitch active={activeTab} onChange={setActiveTab} data-id="element-137" data-mp="2x7oojx31d96c" />
                  </div>
                </div>
              </div>

              {/* Text — RIGHT on desktop */}
              <div className="lg:order-2" data-id="element-138" data-mp="2say46xk77rf4">
                <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-[#1A1A2E] leading-tight mb-6" data-id="element-139" data-mp="dzrk9667p5oc">
                  {t('dashboard.card_title')}
                </h2>
                <ul className="space-y-4 mb-4" data-id="element-140" data-mp="50nytvjkoe6o">
                  {benefits.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-141" data-mp="9ypm6so16zny" data-mpi={index}>
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#EDE7F6] flex items-center justify-center mt-0.5" data-id="element-142" data-mp="xos8es59lben">
                        <Check className="w-3.5 h-3.5 text-[#7C4DFF]" strokeWidth={3} data-id="element-143" data-mp="11qn85s7jrgqb" />
                      </div>
                      <span className="text-gray-700 text-base leading-relaxed" data-id="element-144" data-mp="sxjx1qkdjfyu">
                        {item.text}
                      </span>
                    </li>)}
                </ul>
                <CtaPair data-id="element-145" data-mp="1lemi50uaejuo" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}